import React, { Component } from "react";
import TrimbleMaps from "@trimblemaps/trimblemaps-js";
import MapHelper from "../utils/helper";
import { createRetryHandler, isWebGLAvailable } from "../utils/retryUtils";
import "../styles/mapStyles.scss";
import "@trimblemaps/trimblemaps-js/trimblemaps.css";
import isEqual from "lodash/isEqual"
import appConstants from "../../../../appConstants";
import {MapContext} from "./MapContext"
import { ErrorBoundary } from "../../../../ErrorBoundary";
import MapErrorFallback from "./MapErrorFallback";

class Map extends Component {
  map = null;
  constructor(props) {
    super(props);
    this.state = {
      mapLoaded: false,
      isStyleLoaded: false
    };
    this.retryHandler = createRetryHandler();
  }

  componentDidMount() {
    this.props.eventHub && this.props.eventHub.on("mapResize", this.mapResize);
    this.props.mapConfig && this.initializeMap();
  }

  shouldComponentUpdate(nextProps, nextState){
    return !(isEqual(this.props, nextProps) && isEqual(this.state, nextState))
  }

  initializeMap = () => {
    MapHelper.setConfig({
      ...this.props.mapConfig.trimble,
      enableTrafficWeatherLayer: this.props.mapConfig.enableTrafficWeatherLayer
    });
    TrimbleMaps.APIKey = MapHelper.getKey();
    const style = this.props.satteliteView ? "satellite" : this.props.mapTheme;

    if(this.map){
      this.map.remove();
    }
    if (!isWebGLAvailable()) {
      this.handleMapError();
      return;
    }

    this.map = new TrimbleMaps.Map({
      container: this.props.mapId,
      ...(this.props.center && { center: this.props.center }),
      zoom: this.props.zoom,
      style: MapHelper.getMapStyle(style),
      preserveDrawingBuffer: Boolean(this.props.enablePrint), // required for printing the map
    });
    this.retryHandler.reset();

    this.map?.on("load", () => {
      this.props.eventHub?.emit(`set_${this.props.mapId}`, this.map);
      
      this.setState(
        {
          mapLoaded: true,
        },
        () => {
          this.map?.on("click", this.onMapClicked);
          this.map?.on("styledata", this.onStyleLoaded);
          this.map?.on('mousemove', this.onMouseMove)
        }
      );
    });
  };

  retryMapInitialization = () => {
    this.initializeMap();
  }

  handleMapError = () => {
    this.retryHandler.handleRetry(this.retryMapInitialization);
  }

   onMouseMove = () => {
    const currentZoom = this.map.getZoom();
    const thresholdZoomLevel = this.props.thresholdZoomLevel ?? appConstants.MAP_ZOOM_LEVEL
     if(currentZoom >= thresholdZoomLevel){
     this.props.onMouseMove?.();
     }
   };

  mapResize = () => {
    // TODO : find a better way to handle map resizing when container is resized.
    setTimeout(() => {
      this.map?.resize();
    }, 0);
  };

  componentWillUnmount() {
    this.props.eventHub && this.props.eventHub.off("mapResize", this.mapResize);
    this.retryHandler.cleanup();
    if (this.map) {
      this.map?.off("click", this.onMapClicked);
      this.map?.off("styledata", this.onStyleLoaded);
      this.map?.off('mousemove', this.onMouseMove)
      this.map.remove();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.mapConfig !== prevProps.mapConfig && this.props.mapConfig) {
      this.initializeMap();
    }
    if (this.props.mapTheme !== prevProps.mapTheme) {
      this.setState({
        isStyleLoaded: false,
      });
      this.map?.setStyle(MapHelper.getMapStyle(this.props.mapTheme));
    }
  }

    onStyleLoaded = () => {
        this.setState({
            isStyleLoaded: true
        });
        this.map?.setCenter(this.map?.getCenter());
        if (this.props.getMapStyleName) {
            this.props.getMapStyleName(this.map?.getStyle().name);
        }
    }

  onMapClicked = (event) => {
    if (this.props.onClick) {
      this.props.onClick(event.lngLat);
    }
  };


  render() {
    return (
      <div
        id={this.props.mapId}
        className={`mapContainer theme-${this.props.mapTheme || 'modernLight'}`}>
        {this.map && (
          <MapContext.Provider
            value={{
              map: this.map,
              mapCenter: this.props.center,
              theme: this.props.mapTheme,
              isStyleLoaded: this.state.isStyleLoaded,
              isDefaultSattelite: this.props.satteliteView,
              mapId: this.props.mapId,
            }}>
            {this.props.children}
          </MapContext.Provider>
        )}
        <div id="metrics-container">
          <div id="distance"></div>
          <div id="travelTime"></div>
        </div>
      </div>
    );
  }
}

// Create a wrapper component that handles the container and error boundary
const MapWithErrorBoundary = (props) => (
  <ErrorBoundary errorFallbackComponent={MapErrorFallback}>
    <Map {...props} />
  </ErrorBoundary>
);

export default MapWithErrorBoundary;
